import React from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Package } from "lucide-react";

const LoadingSpinner = () => (
  <div className="text-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
    <p className="text-gray-600 mt-4">Caricamento...</p>
  </div>
);

const EmptyState = ({ title, hint, onCta }) => (
  <div className="text-center py-12">
    <div className="text-gray-400 mb-4">
      <Package className="w-24 h-24 mx-auto" />
    </div>
    <h3 className="text-xl font-medium text-gray-200 mb-2">{title}</h3>
    <p className="text-gray-600 mb-6">{hint}</p>
    <Button onClick={onCta} className="bg-blue-600 hover:bg-blue-700">
      Vai ad Articoli
    </Button>
  </div>
);

/** Admin Preorders tab — articles with availability_type === 'preorder' */
export const PreordersTab = ({ articles, loading, setCurrentView }) => {
  const preorderArticles = articles.filter((a) => a.availability_type === "preorder");
  return (
    <>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">🚀 Gestione Prevendita</h2>
        <p className="text-gray-400">Articoli disponibili in prevendita</p>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : preorderArticles.length === 0 ? (
        <EmptyState
          title="Nessun articolo in prevendita"
          hint='Crea un articolo e imposta "Tipo disponibilità" su "Prevendita"'
          onCta={() => setCurrentView("articles")}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {preorderArticles.map((article) => (
            <Card key={article.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  {article.image && (
                    <img src={article.image} alt={article.title} className="w-20 h-20 object-cover rounded" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">{article.title}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600">Prezzo:</span>
                        <span className="ml-2 font-bold text-green-600">€{article.price.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600">Disponibile dal:</span>
                        <span className="ml-2 font-semibold text-blue-600">
                          {article.preorder_date || "Da definire"}
                        </span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600">Stock:</span>
                        <span className="ml-2">{article.quantity_stock}</span>
                      </div>
                      <Badge className="bg-green-100 text-green-800">🚀 Prevendita</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

/** Admin Reservations tab — articles with availability_type === 'reservation' */
export const ReservationsTab = ({ articles, loading, setCurrentView }) => {
  const reservationArticles = articles.filter((a) => a.availability_type === "reservation");
  return (
    <>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">📋 Gestione Prenotazioni</h2>
        <p className="text-gray-400">Articoli disponibili per prenotazione</p>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : reservationArticles.length === 0 ? (
        <EmptyState
          title="Nessun articolo in prenotazione"
          hint='Crea un articolo e imposta "Tipo disponibilità" su "Prenotazione"'
          onCta={() => setCurrentView("articles")}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reservationArticles.map((article) => (
            <Card key={article.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  {article.image && (
                    <img src={article.image} alt={article.title} className="w-20 h-20 object-cover rounded" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">{article.title}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600">Prezzo:</span>
                        <span className="ml-2 font-bold text-green-600">€{article.price.toFixed(2)}</span>
                      </div>
                      {article.reservation_info && (
                        <div className="text-sm text-gray-600">
                          <span className="font-semibold">Info:</span> {article.reservation_info}
                        </div>
                      )}
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600">Stock:</span>
                        <span className="ml-2">{article.quantity_stock}</span>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800">📋 Prenotazione</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
